import { useState, useCallback, useEffect, useRef } from 'react'
import { useToast } from '@chakra-ui/react'

interface UseApiResourceOptions {
  loadOnMount?: boolean
  errorToastTitle?: string
  toastDuration?: number
}

interface UseApiResourceReturn<T> {
  data: T
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  setData: React.Dispatch<React.SetStateAction<T>>
}

export function useApiResource<T>(
  fetchFn: () => Promise<T>,
  initialData: T,
  options: UseApiResourceOptions = {}
): UseApiResourceReturn<T> {
  const { loadOnMount = true, errorToastTitle = 'Error loading data', toastDuration = 5000 } = options

  const [data, setData] = useState<T>(initialData)
  const [loading, setLoading] = useState(loadOnMount)
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()
  const fetchRef = useRef(fetchFn)
  fetchRef.current = fetchFn

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await fetchRef.current()
      setData(result)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred'
      setError(errorMessage)
      toast({
        title: errorToastTitle,
        description: errorMessage,
        status: 'error',
        duration: toastDuration,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }, [toast, errorToastTitle, toastDuration])

  useEffect(() => {
    if (loadOnMount) {
      refresh()
    }
  }, [loadOnMount, refresh])

  return { data, loading, error, refresh, setData }
}
